import React, { useState, useEffect, useCallback } from 'react';
import { useMutation } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import { StringUtil } from '../../Utils/StringUtil';
import {
    ManagedActionModal,
    ManagedActionModalMode,
    ManagedActionModalState,
    ManagedApiError,
    buildManagedActionModalState,
} from '../Shared/ManagedHelpers';
import {
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Form,
    Input,
    Row,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import Administration from '../../Constants/Endpoints/Administration';
import AUDAddUserRequest from '../../Interfaces/Requests/Administration/AUDAddUserRequest';
import AUDUpdUserRequest from '../../Interfaces/Requests/Administration/AUDUpdUserRequest';
import GetUserAdminModelResponse from '../../Interfaces/Responses/Administration/GetUserAdminModelResponse';

type UserProps = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    userName: string;
    phone: string;
    adminComment: string;
    changePassword: boolean;
    reportsOnly: boolean;
    isSystemAccount: boolean;
    isDemo: boolean;
    securityAdmin: boolean;
    techTeam: boolean;
    newUser: boolean;
    close: () => void;
    refetch: () => void;
};

const User: React.FC<UserProps> = ({ ...props }) => {
    const [firstName, setFirstName] = useState(props.firstName);
    const [lastName, setLastName] = useState(props.lastName);
    const [email, setEmail] = useState(props.email);
    const [password, setPassword] = useState(props.newUser ? props.password : '');
    const [userName, setUserName] = useState(props.userName);
    const [phone, setPhone] = useState(props.phone);
    const [adminComment, setAdminComment] = useState(props.adminComment);
    const [changePasswordRequired, setChangePasswordRequired] = useState(props.changePassword);
    const [reportsOnly, setReportsOnly] = useState(props.reportsOnly);
    const [isSystemAccount, setIsSystemAccount] = useState(props.isSystemAccount);
    const [isDemo, setIsDemo] = useState(props.isDemo);
    const [securityAdmin, setSecurityAdmin] = useState(props.securityAdmin);
    const [techTeam, setTechTeam] = useState(props.techTeam);
    const [submitDisabled, setSubmitDisabled] = useState(true);

    const reinstateValues = useCallback(() => {
        setFirstName(props?.firstName);
        setLastName(props?.lastName);
        setEmail(props?.email);
        setPassword(props?.newUser ? props?.password : '');
        setUserName(props?.userName);
        setPhone(props?.phone);
        setAdminComment(props?.adminComment);
        setChangePasswordRequired(props?.changePassword);
        setReportsOnly(props?.reportsOnly);
        setIsSystemAccount(props?.isSystemAccount);
        setIsDemo(props?.isDemo);
        setSecurityAdmin(props?.securityAdmin);
        setTechTeam(props?.techTeam);
    }, [
        props?.firstName,
        props?.lastName,
        props?.email,
        props?.password,
        props?.newUser,
        props?.userName,
        props?.phone,
        props?.adminComment,
        props?.changePassword,
        props?.reportsOnly,
        props?.isSystemAccount,
        props?.isDemo,
        props?.securityAdmin,
        props?.techTeam,
    ]);

    const [formAddUpdUser] = Form.useForm();

    const [currentError, setCurrentError] = useState('');

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const showUserInfoModal = (
        title: string,
        message: string,
        isError: boolean = true,
        mode: ManagedActionModalMode = 'info'
    ) => {
        setManageActionModalState(buildManagedActionModalState(mode, title, message, isError));
    };

    const getUserActionModalMode = (): ManagedActionModalMode => {
        return props.newUser ? 'add' : 'update';
    };

    const handleManageActionModalOk = () => {
        const shouldCloseAfterDismiss = manageActionModalState.open && !manageActionModalState.isError;

        setManageActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));

        if (shouldCloseAfterDismiss) {
            props.close();
            props.refetch();
        }
    };

    const handleUserFinishFailed = () => {
        showUserInfoModal(
            props.newUser ? 'Create User Error' : 'Update User Error',
            'The User form could not be submitted.  Review the form and try again.',
            true,
            getUserActionModalMode()
        );
    };

    useEffect(() => {
        reinstateValues();
        formAddUpdUser.setFieldValue('addUpdFirstName', props?.firstName);
        formAddUpdUser.setFieldValue('addUpdLastName', props?.lastName);
        formAddUpdUser.setFieldValue('addUpdUserName', props?.userName);
        formAddUpdUser.setFieldValue('addUpdPassword', props?.newUser ? props?.password : '');
        formAddUpdUser.setFieldValue('addUpdEmailAddress', props?.email);
        formAddUpdUser.setFieldValue('addUpdPhone', props?.phone);
        formAddUpdUser.setFieldValue('addUpdAdminComment', props?.adminComment);
    }, [
        reinstateValues,
        formAddUpdUser,
        props?.firstName,
        props?.lastName,
        props?.email,
        props?.password,
        props?.newUser,
        props?.userName,
        props?.phone,
        props?.adminComment,
        props?.changePassword,
        props?.reportsOnly,
        props?.isSystemAccount,
        props?.isDemo,
        props?.securityAdmin,
        props?.techTeam,
    ]);

    const handleSubmitDisabled = useCallback(() => {
        const passwordSatisfied = !props.newUser || !!password.trim();

        if (firstName && lastName && userName && email && passwordSatisfied) {
            setSubmitDisabled(false);
        } else {
            setSubmitDisabled(true);
        }
    }, [firstName, lastName, userName, email, password, props.newUser]);

    useEffect(() => {
        handleSubmitDisabled();
    }, [handleSubmitDisabled, isDemo, securityAdmin, techTeam]);

    const handleFirstNameChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(aValue.target.value);
        handleSubmitDisabled();
    };
    const handleLastNameChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(aValue.target.value);
        handleSubmitDisabled();
    };
    const handleEmailChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(aValue.target.value);
        handleSubmitDisabled();
    };
    const handlePasswordChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(aValue.target.value);
    };
    const handleUserNameChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setUserName(aValue.target.value);
        handleSubmitDisabled();
    };
    const handlePhoneChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(aValue.target.value);
    };
    const handleAdminCommentChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setAdminComment(aValue.target.value);
    };
    const handleChangePasswordRequiredChange = (aValue: { target: { checked: boolean } }) => {
        setChangePasswordRequired(aValue.target.checked);
    };
    const handleReportsOnlyChange = (aValue: { target: { checked: boolean } }) => {
        setReportsOnly(aValue.target.checked);
    };
    const handleIsSystemAccountChange = (aValue: { target: { checked: boolean } }) => {
        setIsSystemAccount(aValue.target.checked);
    };
    const handleIsDemoChange = (aValue: { target: { checked: boolean } }) => {
        setIsDemo(aValue.target.checked);
    };
    const handleSecurityAdminChange = (aValue: { target: { checked: boolean } }) => {
        setSecurityAdmin(aValue.target.checked);
    };
    const handleTechTeamChange = (aValue: { target: { checked: boolean } }) => {
        setTechTeam(aValue.target.checked);
    };

    const validateAddUpdUserName = () => {
        const newAddUpdUserName = StringUtil.ValidateUserName(formAddUpdUser.getFieldValue('addUpdUserName'));

        if (!newAddUpdUserName) {
            return Promise.reject('Invalid User Name');
        } else {
            return Promise.resolve();
        }
    };

    const validateAddUpdPassword = () => {
        const newAddUpdPassword = formAddUpdUser.getFieldValue('addUpdPassword')?.trim();

        if (props.newUser && !newAddUpdPassword) {
            return Promise.reject('Invalid Password');
        } else {
            return Promise.resolve();
        }
    };

    const validateAddUpdEmailAddress = () => {
        const newAddUpdEmailAddress = StringUtil.ValidateEmail(formAddUpdUser.getFieldValue('addUpdEmailAddress'));

        if (!newAddUpdEmailAddress) {
            return Promise.reject('Invalid Email Address');
        } else {
            return Promise.resolve();
        }
    };

    const onFinishAddUpdUser = () => {
        if (props.newUser) {
            addUser();
        } else {
            updUser();
        }
    };

    const { mutate: addUser, isLoading: isAddUserLoading, isError: isAddUserError, error: addUserError, } = useMutation(
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ADD_USER_REQUEST}`;
                const requestData: AUDAddUserRequest = {
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    password: password.trim(),
                    userName: userName,
                    phone: phone,
                    adminComment: adminComment,
                    changePassword: changePasswordRequired,
                    reportsOnly: reportsOnly,
                    isSystemAccount: isSystemAccount,
                    isDemo: isDemo,
                    securityAdmin: securityAdmin,
                    techTeam: techTeam,
                };
                const responseData = await AxiosUtil.post<GetUserAdminModelResponse, AUDAddUserRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('ADD_USER_REQUEST: [Add User] Internal Error: ' + err);
                console.error('ADD_USER_REQUEST [Add User] Internal Error: ', err);
                showUserInfoModal(
                    'Create User Error',
                    `Create User: ${email} Failed. Please Contact Application Support`,
                    true,
                    'add'
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('ADD_USER_REQUEST: [Add User] Internal Error: ' + err);
                console.error('ADD_USER_REQUEST: [Add User] Internal Error: ', err);
                showUserInfoModal(
                    'Create User Error',
                    `Create User: ${email} Failed. Please Contact Application Support`,
                    true,
                    'add'
                );
            },
            onSuccess: (response: GetUserAdminModelResponse) => {
                showUserInfoModal(
                    'Create User',
                    `Create User: ${email} Succeeded.`,
                    false,
                    'add'
                );
            },
        }
    );

    const { mutate: updUser, isLoading: isUpdUserLoading, isError: isUpdUserError, error: updUserError, } = useMutation(
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.UPD_USER_REQUEST}`;
                const requestData: AUDUpdUserRequest = {
                    id: props.id,
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    password: password.trim(),
                    userName: userName,
                    phone: phone,
                    adminComment: adminComment,
                    changePassword: changePasswordRequired,
                    reportsOnly: reportsOnly,
                    isSystemAccount: isSystemAccount,
                    isDemo: isDemo,
                    securityAdmin: securityAdmin,
                    techTeam: techTeam,
                };
                const responseData = await AxiosUtil.post<GetUserAdminModelResponse, AUDUpdUserRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('UPD_USER_REQUEST: [Upd User] Internal Error: ' + err);
                console.error('UPD_USER_REQUEST: [Upd User] Internal Error: ', err);
                showUserInfoModal(
                    'Update User Error',
                    `Update User: ${email} Failed. Please Contact Application Support`,
                    true,
                    'update'
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('UPD_USER_REQUEST: [Upd User] Internal Error: ' + err);
                console.error('UPD_USER_REQUEST: [Upd User] Internal Error: ', err);
                showUserInfoModal(
                    'Update User Error',
                    `Update User: ${email} Failed. Please Contact Application Support`,
                    true,
                    'update'
                );
            },
            onSuccess: (response: GetUserAdminModelResponse) => {
                showUserInfoModal(
                    'Update User',
                    `Update User: ${email} Succeeded.`,
                    false,
                    'update'
                );
            },
        }
    );

    const handleCancel = () => {
        reinstateValues();
        props.close();
    };

    const getUActionColor = (): string => {
        return props.newUser ? 'cornflowerblue' : 'darkgray';
    };

    const determineWhichError = () => {
        if ( isAddUserError ) {
            return currentError + ' ' + ((addUserError as ManagedApiError).response?.data?.message ?? '');
        }
        else if ( isUpdUserError ) {
            return currentError + ' ' + ((updUserError as ManagedApiError).response?.data?.message ?? '');
        }
        else {
            return '';
        }
    };

    return (
        <>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageActionModalOk}
                onCancel={handleManageActionModalOk}
            />
            <Spin spinning={
                            isAddUserLoading ||
                            isUpdUserLoading
                            }>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isAddUserError ||
                        isUpdUserError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: getUActionColor(), borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>{props.newUser ? 'Add New User' : 'Update Existing User'}</Title>
                    </Text>
                </Space>
                <Form
                    form={formAddUpdUser}
                    key='userdetails'
                    onFinish={onFinishAddUpdUser}
                    onFinishFailed={handleUserFinishFailed}
                >
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='aufirstname'
                                    label='First Name'
                                    labelCol={{ span: 7, }}
                                    name='addUpdFirstName'
                                    rules={[
                                        {
                                            message: 'First Name Required.',
                                            required: true,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={firstName}
                                        onChange={handleFirstNameChange}
                                        placeholder='Enter First Name'
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='aulastname'
                                    label='Last Name'
                                    labelCol={{ span: 7, }}
                                    name='addUpdLastName'
                                    rules={[
                                        {
                                            message: 'Last Name Required.',
                                            required: true,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={lastName}
                                        onChange={handleLastNameChange}
                                        placeholder='Enter Last Name'
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='auusername'
                                    label='User Name'
                                    labelCol={{ span: 7, }}
                                    name='addUpdUserName'
                                    rules={[
                                        {
                                            message: 'User Name Required.',
                                            required: true,
                                            validator: validateAddUpdUserName,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={userName}
                                        onChange={handleUserNameChange}
                                        placeholder='Enter User Name'
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='aupassword'
                                    label={props.newUser ? 'Password' : 'New Password'}
                                    labelCol={{ span: 7, }}
                                    name='addUpdPassword'
                                    rules={[
                                        {
                                            message: 'Password Required.',
                                            required: props.newUser,
                                            validator: validateAddUpdPassword,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        type='password'
                                        value={password}
                                        onChange={handlePasswordChange}
                                        placeholder={props.newUser ? 'Enter Password' : 'Leave blank to keep existing password'}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='auemailaddress'
                                    label='Email Address'
                                    labelCol={{ span: 7, }}
                                    name='addUpdEmailAddress'
                                    rules={[
                                        {
                                            message: 'Email Address Required.',
                                            required: true,
                                            validator: validateAddUpdEmailAddress,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={email}
                                        onChange={handleEmailChange}
                                        placeholder='Enter Email'
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='auphone'
                                    label='Phone'
                                    labelCol={{ span: 7, }}
                                    name='addUpdPhone'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={phone}
                                        onChange={handlePhoneChange}
                                        placeholder='Enter Phone Number'
                                        type='tel'
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='auadmincomment'
                                    label='Admin Comment'
                                    labelCol={{ span: 7, }}
                                    name='addUpdAdminComment'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={adminComment}
                                        onChange={handleAdminCommentChange}
                                        placeholder='Enter Admin Comment'
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <hr />
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={12}>
                                <Checkbox
                                    checked={isSystemAccount}
                                    onChange={handleIsSystemAccountChange}
                                >
                                    Is System Account
                                </Checkbox>
                            </Col>
                            <Col span={12}>
                                <Checkbox
                                    checked={changePasswordRequired}
                                    onChange={handleChangePasswordRequiredChange}
                                >
                                    Change Password Required
                                </Checkbox>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={12}>
                                <Checkbox
                                    checked={isDemo}
                                    onChange={handleIsDemoChange}
                                >
                                    Is Demo Account
                                </Checkbox>
                            </Col>
                            <Col span={12}>
                                <Checkbox
                                    checked={reportsOnly}
                                    onChange={handleReportsOnlyChange}
                                >
                                    Reports Only
                                </Checkbox>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={12}>
                                <Checkbox
                                    checked={securityAdmin}
                                    onChange={handleSecurityAdminChange}
                                >
                                    Is Security Admin
                                </Checkbox>
                            </Col>
                            <Col span={12}>
                                <Checkbox
                                    checked={techTeam}
                                    onChange={handleTechTeamChange}
                                >
                                    Is Tech. Team
                                </Checkbox>
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col span={16} />
                            <Col span={4}>
                                <Form.Item>
                                    <Button
                                        disabled={submitDisabled}
                                        htmlType='submit'
                                        style={{backgroundColor: getUActionColor(), float: 'right'}}
                                        type='primary'
                                    >
                                        Submit
                                    </Button>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Button onClick={handleCancel}>Cancel</Button>
                            </Col>
                        </Row>
                    </div>
                </Form>
            </Spin>
        </>
    );
};

export default User;
